/**
 * What you are about to sit with, on the home screen, redrawn as you choose.
 *
 * This was a second screen between Begin and the sit. It is on the home screen now,
 * above the button, so the length chips and the cadence change the band in front of
 * you rather than on a page you have not reached yet — and the tap that used to open
 * the preview starts the sit.
 *
 * It keeps the preview's two jobs in less room. The figures, because decision 1.3 says
 * the user sees real numbers and not a mood. And provenance — the date is not metadata,
 * it is the reason to open this rather than a studio recording made last quarter, and
 * the sentence under the band is the honest disclosure that this recording has been
 * adapted. The home screen has one viewport and no scrollbar, so the recorded-length
 * table and the themes did not come along; the sentence already says what they did.
 */

import type { Recording, SessionPlan } from '../types';
import { formatPreview } from '../state/settings';
import { Timeline } from './Timeline';

interface Props {
  recording: Recording | null;
  /** Null when this recording cannot be made the chosen length; `problem` says why. */
  plan: SessionPlan | null;
  problem: string | null;
  onSomethingElse: () => void;
}

function shortDate(iso: string, precision: Recording['datePrecision']): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Date unknown';
  if (precision === 'year') return `Sometime in ${d.getFullYear()}`;
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  if (precision === 'published') return `Published ${date}`;
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${date}`;
}

/** What the plan did to the silences, in the words read before every sit. */
export function silenceSentence(nativeSec: number, actualSec: number): string {
  const delta = Math.abs(Math.round((actualSec - nativeSec) / 60));
  if (delta === 0) return 'Played at its own length; spoken passages stay at recorded speed';
  // A one-minute change said "1 minutes", and it was on the screen people read before
  // every sit — and in the Play store screenshots.
  const amount = `${delta} ${delta === 1 ? 'minute' : 'minutes'}`;
  return actualSec > nativeSec
    ? `Recording lengthened by ${amount}; spoken passages stay at recorded speed`
    : `Recording shortened by ${amount}; spoken passages stay at recorded speed`;
}

export function SitPreview({ recording, plan, problem, onSomethingElse }: Props) {
  return (
    <section class="sit-preview" aria-label="Your sit">
      <div class="spread sit-preview-head">
        <span class="eyebrow sit-preview-date">
          {recording && shortDate(recording.recordedOn, recording.datePrecision)}
        </span>
        <button class="link" onClick={onSomethingElse}>Something else</button>
      </div>
      <div class="serif sit-preview-title">{recording?.title}</div>

      {plan && recording ? (
        <>
          <Timeline plan={plan} nativeSec={recording.nativeSec} height={36} />
          {/* One paragraph rather than two, so the two halves share a wrapped line on a
              narrow phone instead of each taking two. */}
          <p class="sit-preview-note">
            <span class="sit-preview-figures">{formatPreview(plan.preview)}. </span>
            <span data-testid="silence-sentence">
              {silenceSentence(recording.nativeSec, plan.actualSec)}
              {plan.preview.cueTrimmed && '; his sentence naming a different length is left out'}.
            </span>
          </p>
          {plan.preview.speechRatioAdjustment && (
            <p class="dim" data-testid="speech-ratio-policy" style={{ fontSize: 'var(--step--1)' }}>
              {plan.preview.speechRatioAdjustment.applied
                ? 'Speech balance uses reviewed shorter boundary passages.'
                : plan.preview.speechRatioAdjustment.reason === 'no-approved-variants'
                  ? 'Speech balance stays with the full recorded boundaries; no shorter reviewed version is available.'
                  : 'The full recorded boundaries are already the closest reviewed speech balance.'}
            </p>
          )}
        </>
      ) : (
        <p class="sit-preview-note" role="status" style={{ color: 'var(--warn)' }}>{problem}</p>
      )}
    </section>
  );
}
